import { DataChart } from "@repo/ui/charts/dataChart";
import { useEffect, useState } from "react";

import { WidgetMapChart } from "../widgets/maps/widgetMapChart";
import { exportModel, type ExportInput } from "./renderModel";

// How long to wait for the chart SVG to appear before reporting an error to the worker.
const chartTimeoutMs = 15_000;

/**
 * Standalone render page for the MyView V2 automated export. Mounted by main.tsx when the root element
 * has data-page="my-view-v2-widget" (see MVC/Views/MyViewV2/Widget.cshtml) with the widget payload the
 * MyViewExportV2Controller embedded in #myview-export-data.
 *
 * The C# worker (TLP.Sync.AutomatedWidgetExport.ReactExports) polls the #myview-v2-result JSON contract
 * until state is "ready" and then screenshots #myview-v2-capture for charts, heatmaps and maps. It never
 * inspects widget DOM, so the layout here can change freely as long as the contract is kept.
 */
export function ExportPage({ input }: { input: ExportInput }) {
	const [model] = useState(() => exportModel(input));
	const [mapReady, setMapReady] = useState(false);
	const [error, setError] = useState<string>();
	const [ready, setReady] = useState(false);
	useEffect(() => {
		if (error) return;
		if (model.map && model.kind !== "empty" && !mapReady) return;
		let active = true;
		const started = Date.now();
		const waitForPaint = () =>
			requestAnimationFrame(() =>
				requestAnimationFrame(() => {
					if (active) setReady(true);
				}),
			);
		// Recharts measures its container asynchronously, so wait for the SVG before declaring ready.
		const waitForChart = () => {
			if (!active) return;
			if (document.querySelector("#myview-v2-capture svg")) waitForPaint();
			else if (Date.now() - started > chartTimeoutMs) setError("Chart did not render.");
			else requestAnimationFrame(waitForChart);
		};
		void document.fonts.ready.then(() => {
			if (model.kind === "chart") waitForChart();
			else waitForPaint();
		});
		return () => {
			active = false;
		};
	}, [model, mapReady, error]);
	return (
		<main
			id="myview-v2-capture"
			style={{
				width: 900,
				boxSizing: "border-box",
				padding: 24,
				background: "white",
				color: "#111827",
				fontFamily: "Arial, sans-serif",
			}}
		>
			<style>{`#myview-v2-capture *, #myview-v2-capture *::before { animation: none !important; transition: none !important; } #myview-v2-capture .drop { opacity: 1 !important; }`}</style>
			<h1 style={{ fontSize: 24, marginBottom: 16 }}>{input.title}</h1>
			{model.kind === "empty" ? (
				<p>No data for this timeframe and filters.</p>
			) : model.map ? (
				<WidgetMapChart
					id="export-map"
					data={model.map.markers}
					type={model.map.type}
					height={600}
					onExportReady={() => setMapReady(true)}
					onExportError={() => setError("Map failed to load.")}
				/>
			) : model.chart ? (
				// Flex column so DataChart (flex: 1 1 auto) grows to fill the capture height.
				<div style={{ height: 600, display: "flex", flexDirection: "column" }}>
					<DataChart model={model.chart} title={input.title} />
				</div>
			) : (
				<table>
					<thead>
						<tr>
							{model.headers.map((h, i) => (
								<th key={i}>{h}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{model.displayCells.map((row, i) => (
							<tr key={i}>
								{row.map((cell, j) => (
									<td key={j}>{cell}</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			)}
			<script id="myview-v2-result" type="application/json">
				{JSON.stringify({
					version: 2,
					state: error ? "error" : ready ? "ready" : "loading",
					error: error ?? null,
					kind: model.kind,
					headers: model.headers,
					cells: model.cells,
					displayCells: model.displayCells,
				})}
			</script>
		</main>
	);
}
